import type { CatalogSource, CatalogSourceInput, CatalogProbe, FeatureMode, FeaturePolicy } from "@player115/contracts";
import { Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../api";
import { PageState, Status } from "../components/Status";

const modes: Array<{ id: FeatureMode; label: string }> = [
  { id: "open", label: "免费开放" },
  { id: "entitlement", label: "需要权益" },
  { id: "disabled", label: "全局停用" },
];

const ruleOptions: Array<{ id: CatalogSource["ruleKey"]; label: string }> = [
  { id: "generic", label: "通用 HTML" },
  { id: "mukaku", label: "木卡库 API" },
  { id: "dytt8", label: "电影天堂规则" },
  { id: "dy131", label: "6V 规则" },
  { id: "btbtla", label: "BT 联盟规则" },
  { id: "mjf2020", label: "美剧天堂规则" },
];

const originalNames: Record<string, string> = {
  cloud: "115 云端",
  library: "影库",
  catalog: "片库",
  music: "乐库",
  ai_subtitles: "AI 字幕",
};

const healthLabels = {available:"可访问",restricted:"访问受限",unavailable:"不可访问"} as const;
const probeSummary = (probe: CatalogProbe) => (probe.httpStatus ? "HTTP " + probe.httpStatus : probe.message) + " · " + probe.latencyMs + " ms";

export function FeaturesPage() {
  const [features, setFeatures] = useState<FeaturePolicy[]>([]);
  const [sources, setSources] = useState<CatalogSource[]>([]);
  const [busyKey, setBusyKey] = useState("");
  const [monitorError, setMonitorError] = useState("");
  const [checking, setChecking] = useState<string[]>([]);
  const [savedKey, setSavedKey] = useState("");
  const [error, setError] = useState("");
  const [showSourceForm, setShowSourceForm] = useState(false);
  const [newSource, setNewSource] = useState<CatalogSourceInput>({ sourceKey: "", originalName: "", displayName: "", feedUrl: "", homeUrl: "", ruleKey: "generic", enabled: true, sortOrder: 50 });
  const load = async () => {
    setError("");
    try {
      const [featureResult, sourceResult] = await Promise.all([api.features(), api.catalogSources()]);
      setFeatures(featureResult.features); setSources(sourceResult.sources);
    }
    catch (reason) { setError(reason instanceof Error ? reason.message : "模块策略读取失败"); }
  };
  const updateSource = (key: string, patch: Partial<CatalogSource>) => {
    setSavedKey("");
    setSources(current => current.map(source => source.sourceKey === key ? {
      ...source, ...patch, ...(patch.feedUrl !== undefined || patch.homeUrl !== undefined ? {health:null} : {}),
    } : source));
  };
  const checkSource = async (source: CatalogSource) => {
    setChecking(current => [...current,source.sourceKey]);
    try {
      const result = await api.checkCatalogSource(source);
      setSources(current => current.map(item => item.sourceKey === source.sourceKey && item.feedUrl === source.feedUrl && item.homeUrl === source.homeUrl
        ? {...item,health:result.source.health} : item));
    } catch (reason) { setError(reason instanceof Error ? reason.message : "可用性检测失败"); }
    finally { setChecking(current => current.filter(key => key !== source.sourceKey)); }
  };
  const checkAllSources = async () => {
    setError("");
    // 分批两站，不向源站瞬间发出大量检测请求。
    for (let index=0;index<sources.length;index+=2) await Promise.all(sources.slice(index,index+2).map(checkSource));
  };
  const saveSource = async (source: CatalogSource) => {
    setBusyKey(source.sourceKey); setError("");
    try { const { updatedAt: _updatedAt, ...input } = source; const result = await api.saveCatalogSource(input); setSources((current) => current.map((item) => item.sourceKey === source.sourceKey ? result.source : item)); setSavedKey(source.sourceKey); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "资源站配置保存失败"); }
    finally { setBusyKey(""); }
  };
  const createSource = async () => {
    setError("");
    try {
      const result = await api.createCatalogSource(newSource);
      setSources((current) => [...current, result.source].sort((a, b) => a.sortOrder - b.sortOrder || a.sourceKey.localeCompare(b.sourceKey)));
      setNewSource({ sourceKey: "", originalName: "", displayName: "", feedUrl: "", homeUrl: "", ruleKey: "generic", enabled: true, sortOrder: 50 });
      setShowSourceForm(false); setSavedKey(result.source.sourceKey);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "资源站配置创建失败"); }
  };
  const deleteSource = async (source: CatalogSource) => {
    if (!window.confirm(`确认删除“${source.originalName}”的后台配置吗？`)) return;
    setBusyKey(source.sourceKey); setError("");
    try { await api.deleteCatalogSource(source.sourceKey); setSources((current) => current.filter((item) => item.sourceKey !== source.sourceKey)); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "资源站配置删除失败"); }
    finally { setBusyKey(""); }
  };
  useEffect(() => { void load(); }, []);
  useEffect(() => {
    let active=true;
    const timer=window.setInterval(()=>{
      if(document.hidden)return;
      void api.catalogSources().then(result=>{
        if(!active)return;
        setMonitorError("");
        setSources(current=>current.map(item=>{
          const latest=result.sources.find(source=>source.sourceKey===item.sourceKey);
          return latest && latest.feedUrl===item.feedUrl && latest.homeUrl===item.homeUrl ? {...item,health:latest.health} : item;
        }));
      }).catch(()=>{if(active)setMonitorError("监测状态刷新失败，当前显示上次记录");});
    },30_000);
    return()=>{active=false;window.clearInterval(timer);};
  },[]);
  const update = (featureKey: string, patch: Partial<FeaturePolicy>) => {
    setFeatures((current) => current.map((feature) => feature.featureKey === featureKey ? { ...feature, ...patch } : feature));
    setSavedKey("");
  };
  const save = async (feature: FeaturePolicy) => {
    setBusyKey(feature.featureKey);
    setError("");
    try {
      const { updatedAt: _updatedAt, ...input } = feature;
      const result = await api.saveFeature(input);
      update(feature.featureKey, result.feature);
      setSavedKey(feature.featureKey);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "模块策略保存失败"); }
    finally { setBusyKey(""); }
  };
  const saveAll = async () => {
    setBusyKey("all");
    setError("");
    try {
      const [featureResults, sourceResults] = await Promise.all([
        Promise.all(features.map(async ({ updatedAt: _updatedAt, ...input }) => (await api.saveFeature(input)).feature)),
        Promise.all(sources.map(async ({ updatedAt: _updatedAt, ...input }) => (await api.saveCatalogSource(input)).source)),
      ]);
      setFeatures(featureResults);
      setSources(sourceResults);
      setSavedKey("all");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "批量保存失败");
    } finally {
      setBusyKey("");
    }
  };
  return <section className="page features-page">
    <header className="page-head"><div><h1>模块权限</h1><p>控制客户端模块的开放、权益和停用状态</p></div><Status tone="warning">权益核销待支付入口确定</Status></header>
    {error && <div className="inline-error"><span>{error}</span><button type="button" onClick={() => void load()}><RefreshCw size={15} />重试</button></div>}
    {features.length ? <div className="feature-table">
      <div className="feature-table-head"><span>模块</span><span>访问策略</span><span>锁定提示</span><span>操作</span></div>
      {features.map((feature) => <article className="feature-row" key={feature.featureKey}>
        <div className="feature-name"><label><span>客户端显示名称</span><input value={feature.displayName} onChange={(event) => update(feature.featureKey, { displayName: event.target.value })} /></label><small>{feature.description}</small><small>原始模块：{originalNames[feature.featureKey] || feature.featureKey}</small><code>{feature.featureKey}</code></div>
        <div className="mode-control" role="group" aria-label={`${feature.displayName}访问策略`}>{modes.map((mode) => <button className={feature.mode === mode.id ? `is-selected mode-${mode.id}` : ""} type="button" aria-pressed={feature.mode === mode.id} key={mode.id} onClick={() => update(feature.featureKey, { mode: mode.id })}>{mode.label}</button>)}</div>
        <label><span className="mobile-label">锁定提示</span><input aria-label={`${feature.displayName}锁定提示`} value={feature.lockedMessage} onChange={(event) => update(feature.featureKey, { lockedMessage: event.target.value })} /></label>
        <div className="feature-action">{savedKey === feature.featureKey && <Status tone="success">已保存</Status>}<button className="icon-button" type="button" title={`保存${feature.displayName}`} disabled={busyKey === feature.featureKey} onClick={() => void save(feature)}>{busyKey === feature.featureKey ? <RefreshCw className="spin" size={17} /> : <Save size={17} />}</button></div>
      </article>)}
    </div> : !error && <PageState title="暂无模块策略" detail="数据库初始化后会创建默认模块。" />}
    <section className="catalog-source-settings">
      <header><div><h2>片库资源站</h2><p>TV/手机使用备注名称；后台保留原始站名、地址和抓取规则供维护。</p></div>
        <div className="catalog-source-actions">
          {savedKey === "all" && <Status tone="success">全部已保存，客户端将在下次策略刷新时同步</Status>}
          <button className="soft-button" type="button" disabled={checking.length>0 || !sources.length} onClick={() => void checkAllSources()}><RefreshCw size={16} className={checking.length?"spin":""}/>{checking.length?"正在检测":"检测全部"}</button>
          <button className="primary-button" type="button" disabled={busyKey === "all" || !features.length} onClick={() => void saveAll()}><Save size={16}/>保存全部</button>
          <button className="soft-button" type="button" onClick={() => setShowSourceForm(current => !current)}><Plus size={16}/>添加资源站</button>
        </div>
      </header>
      <p className="catalog-monitor-note">启用的来源每 15 分钟巡检；检测使用已保存地址，失败不会自动停用。HTTP 可访问不代表内容抓取一定成功。</p>
      {monitorError && <p className="catalog-monitor-note" role="status">{monitorError}</p>}
      {showSourceForm && <div className="catalog-source-create">
        <label><span>标识</span><input value={newSource.sourceKey} onChange={e=>setNewSource(current=>({...current,sourceKey:e.target.value}))} placeholder="例如 movie-source"/></label>
        <label><span>后台原始名称</span><input value={newSource.originalName} onChange={e=>setNewSource(current=>({...current,originalName:e.target.value}))} placeholder="仅后台可见"/></label>
        <label><span>备注名称（客户端）</span><input maxLength={40} value={newSource.displayName||""} onChange={e=>setNewSource(current=>({...current,displayName:e.target.value}))} placeholder="例如 精选片库"/></label>
        <label><span>抓取规则</span><select value={newSource.ruleKey} onChange={e=>setNewSource(current=>({...current,ruleKey:e.target.value as CatalogSource["ruleKey"]}))}>{ruleOptions.map(rule=><option key={rule.id} value={rule.id}>{rule.label}</option>)}</select></label>
        <label><span>列表地址</span><input value={newSource.feedUrl} onChange={e=>setNewSource(current=>({...current,feedUrl:e.target.value}))} placeholder="https://example.com/list.html"/></label>
        <label><span>站点地址</span><input value={newSource.homeUrl} onChange={e=>setNewSource(current=>({...current,homeUrl:e.target.value}))} placeholder="https://example.com/"/></label>
        <label><span>排序</span><input type="number" min="0" max="10000" value={newSource.sortOrder} onChange={e=>setNewSource(current=>({...current,sortOrder:Number(e.target.value)||0}))}/></label>
        <label className="catalog-source-enabled"><span>启用</span><input type="checkbox" checked={newSource.enabled} onChange={e=>setNewSource(current=>({...current,enabled:e.target.checked}))}/></label>
        <button className="primary-button" type="button" disabled={!newSource.displayName?.trim()} onClick={()=>void createSource()}>保存资源站</button>
      </div>}
      <div className="catalog-source-table">{sources.map(source=><article className="catalog-source-row" key={source.sourceKey}>
        <div className="catalog-source-identity"><strong>{source.originalName}</strong><code>{source.sourceKey}</code></div>
        <label><span>备注名称（客户端）</span><input aria-label={source.originalName+"备注名称"} maxLength={40} value={source.displayName} onChange={e=>updateSource(source.sourceKey,{displayName:e.target.value})} placeholder="客户端显示名称"/></label>
        <label><span>抓取规则</span><select value={source.ruleKey} onChange={e=>updateSource(source.sourceKey,{ruleKey:e.target.value as CatalogSource["ruleKey"]})}>{ruleOptions.map(rule=><option key={rule.id} value={rule.id}>{rule.label}</option>)}</select></label>
        <label><span>列表地址</span><input value={source.feedUrl} onChange={e=>updateSource(source.sourceKey,{feedUrl:e.target.value})}/></label>
        <label><span>站点地址</span><input value={source.homeUrl} onChange={e=>updateSource(source.sourceKey,{homeUrl:e.target.value})}/></label>
        <label className="catalog-source-enabled"><span>启用</span><input type="checkbox" checked={source.enabled} onChange={e=>updateSource(source.sourceKey,{enabled:e.target.checked})}/></label>
        <div className="catalog-source-controls">
          <button className="icon-button" type="button" title={"检测"+source.originalName} disabled={checking.includes(source.sourceKey)} onClick={()=>{setError("");void checkSource(source);}}><RefreshCw size={17} className={checking.includes(source.sourceKey)?"spin":""}/></button>
          <button className="icon-button" type="button" title={"保存"+source.originalName} disabled={busyKey===source.sourceKey || busyKey==="all" || !source.displayName.trim()} onClick={()=>void saveSource(source)}><Save size={17}/></button>
          <button className="icon-button danger" type="button" title={"删除"+source.originalName} disabled={busyKey===source.sourceKey || busyKey==="all"} onClick={()=>void deleteSource(source)}><Trash2 size={17}/></button>
        </div>
        <div className="catalog-source-health" aria-live="polite">
          {checking.includes(source.sourceKey)?<Status tone="muted">正在检测</Status>:source.health?<>
            <Status tone={source.health.state==="available"?"success":source.health.state==="restricted"?"warning":"danger"}>{healthLabels[source.health.state]}</Status>
            <span title={source.health.home.message}>站点：{probeSummary(source.health.home)}</span>
            <span title={source.health.feed.message}>列表：{probeSummary(source.health.feed)}</span>
            <time dateTime={source.health.checkedAt}>检测于 {new Date(source.health.checkedAt).toLocaleString("zh-CN",{hour12:false})}</time>
          </>:<span>尚未检测</span>}
          {savedKey===source.sourceKey && <Status tone="success">已保存</Status>}
        </div>
      </article>)}</div>
    </section>
  </section>;
}
